export const ADMIN_ROLE = {
  SUPER: 'ADMIN_SUPER',
  L1: 'ADMIN_L1',
  L2: 'ADMIN_L2',
  L3: 'ADMIN_L3',
};

export const ADMIN_ROLE_OPTIONS = [
  { value: ADMIN_ROLE.SUPER, label: '超管' },
  { value: ADMIN_ROLE.L1, label: '一级' },
  { value: ADMIN_ROLE.L2, label: '二级' },
  { value: ADMIN_ROLE.L3, label: '三级' },
];

/** 后台用户页权限说明（与后端 AdminSecurity 规则一致） */
export const ADMIN_ROLE_PERMISSION_GUIDE = [
  {
    label: '超管',
    summary: '全部模块可增删改查',
    userCenter: '增删改查',
    adminUsers: '增删改查',
    otherModules: '增删改查',
  },
  {
    label: '一级',
    summary: '用户中心可操作，可添加后台管理账号；后台用户编辑/删除仅超管',
    userCenter: '增删改查',
    adminUsers: '可添加，编辑/删除仅超管',
    otherModules: '增删改查',
  },
  {
    label: '二级',
    summary: '用户中心仅查看，其他模块可操作',
    userCenter: '仅查看',
    adminUsers: '仅查看',
    otherModules: '增删改查',
  },
  {
    label: '三级',
    summary: '全部模块仅查看，无操作权限',
    userCenter: '仅查看',
    adminUsers: '仅查看',
    otherModules: '仅查看',
  },
];

const ADMIN_ROLE_KEY = 'ipe_admin_role';
const ADMIN_PERMISSIONS_KEY = 'ipe_admin_permissions';

export function normalizeAdminRole(role) {
  if (!role) return null;
  const raw = String(role).trim();
  const upper = raw.toUpperCase();
  if (upper === 'ADMIN_SUPER' || upper === 'ADMIN' || raw === '管理员' || raw === '超管') return ADMIN_ROLE.SUPER;
  if (upper === 'ADMIN_L1' || raw === '一级') return ADMIN_ROLE.L1;
  if (upper === 'ADMIN_L2' || raw === '二级') return ADMIN_ROLE.L2;
  if (upper === 'ADMIN_L3' || raw === '三级') return ADMIN_ROLE.L3;
  return null;
}

export function adminRoleLabel(role) {
  const normalized = normalizeAdminRole(role);
  return ADMIN_ROLE_OPTIONS.find((item) => item.value === normalized)?.label || role || '-';
}

export function adminRolePermissionSummary(role) {
  const label = adminRoleLabel(role);
  const item = ADMIN_ROLE_PERMISSION_GUIDE.find((row) => row.label === label);
  return item?.summary || '-';
}

export function isAdminPortalRole(role) {
  return normalizeAdminRole(role) != null;
}

export function setAdminSessionMeta(role, permissions) {
  if (typeof window === 'undefined') return;
  const normalized = normalizeAdminRole(role);
  if (normalized) {
    localStorage.setItem(ADMIN_ROLE_KEY, normalized);
  } else {
    localStorage.removeItem(ADMIN_ROLE_KEY);
  }
  if (permissions && typeof permissions === 'object') {
    localStorage.setItem(ADMIN_PERMISSIONS_KEY, JSON.stringify(permissions));
  } else {
    localStorage.removeItem(ADMIN_PERMISSIONS_KEY);
  }
}

export function clearAdminSessionMeta() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ADMIN_ROLE_KEY);
  localStorage.removeItem(ADMIN_PERMISSIONS_KEY);
}

export function getAdminRole() {
  if (typeof window === 'undefined') return null;
  return normalizeAdminRole(localStorage.getItem(ADMIN_ROLE_KEY));
}

function readPermissions() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ADMIN_PERMISSIONS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getAdminPermissions() {
  const stored = readPermissions();
  if (stored) return stored;
  const role = getAdminRole();
  return {
    canView: isAdminPortalRole(role),
    canWriteUserCenter: canWriteUserCenter(role),
    canWriteAdminUsers: canWriteAdminUsers(role),
    canCreateAdminUsers: canCreateAdminUsers(role),
    canWriteOtherModules: canWriteOtherModules(role),
    readOnly: isReadOnlyAdmin(role),
  };
}

export function canWriteUserCenter(role = getAdminRole()) {
  const normalized = normalizeAdminRole(role);
  return normalized === ADMIN_ROLE.SUPER || normalized === ADMIN_ROLE.L1;
}

export function canWriteAdminUsers(role = getAdminRole()) {
  return normalizeAdminRole(role) === ADMIN_ROLE.SUPER;
}

/** 节点 11.1：系统模特「从静态资源导入」仅超管可用（对齐后端 requireSuperAdmin） */
export function canImportSystemModelsFromStatic(role = getAdminRole()) {
  return canWriteAdminUsers(role);
}

/** 超管、一级管理员可添加后台管理账号 */
export function canCreateAdminUsers(role = getAdminRole()) {
  const normalized = normalizeAdminRole(role);
  return normalized === ADMIN_ROLE.SUPER || normalized === ADMIN_ROLE.L1;
}

export function canWriteOtherModules(role = getAdminRole()) {
  const normalized = normalizeAdminRole(role);
  return normalized === ADMIN_ROLE.SUPER || normalized === ADMIN_ROLE.L1 || normalized === ADMIN_ROLE.L2;
}

export function isReadOnlyAdmin(role = getAdminRole()) {
  return normalizeAdminRole(role) === ADMIN_ROLE.L3;
}

export function canMutateUserRow(row, activeTab, role = getAdminRole()) {
  if (isReadOnlyAdmin(role)) return false;
  if (activeTab === 'admins') {
    return canWriteAdminUsers(role);
  }
  return canWriteUserCenter(role);
}

export function canCreateUser(activeTab, role = getAdminRole()) {
  if (activeTab === 'membership' || activeTab === 'shops') return false;
  if (activeTab === 'admins') return canCreateAdminUsers(role);
  return canWriteUserCenter(role);
}
